import { Table, Typography, DatePicker, Tooltip, Input, message, Button } from "antd"
import { useEffect, useState } from "react"

//react router dom's link as Redirect due to antd Typography Link
import { Link as Redirect } from "react-router-dom"
import { designerUsersFilter } from "../../../components/Filters/TableFilters"
import { collection, onSnapshot, orderBy, query, where } from "firebase/firestore"
import { db } from "../../../firebase/config"
import { OrderAndPrefix } from "../../../components/Orders/Helpers/OrderAndPrefix"
import TechnicalPageModal from "./TechnicalPageModal"
import { orderEndDate, orderStartDate } from "../../../components/Table/orderDateDisplay"
import { returnProperMachineTag } from "../../../components/utils/ReturnProperMachineTag"
import { SearchOutlined } from "@ant-design/icons"
import { getDownloadURL, getStorage, ref } from "firebase/storage"

const { Text, Link } = Typography
const { RangePicker } = DatePicker

export default function DesignerOrderTable(props) {
  const [activeOrders, setActiveOrders] = useState([])
  const [activeOrdersLoading, setActiveOrdersLoading] = useState(false)
  const [dateRange, setDateRange] = useState([null, null])
  const [endDateRange, setEndDateRange] = useState([null, null])

  useEffect(() => {
    setActiveOrdersLoading(true)
    const ordersRef = collection(db, "orders")

    const activeQ = query(
      ordersRef,
      orderBy("orderStatus"),
      where("orderStatus", "==", props.activeKey),
    )

    const unsubscribe = onSnapshot(activeQ, (snapshot) => {
      let results = []
      snapshot.docs.forEach((doc) => {
        results.push({ ...doc.data(), id: doc.id })
      })
      setActiveOrders(results)
      setActiveOrdersLoading(false)
    })
    return () => unsubscribe()
  }, [props.activeKey])

  const filteredOrders = activeOrders.filter((order) => {
    if (dateRange[0] && dateRange[1]) {
      if (!order.created?.seconds) return false
      const orderSeconds = order.created.seconds
      const start = dateRange[0].startOf("day").unix()
      const end = dateRange[1].endOf("day").unix()
      if (!(orderSeconds >= start && orderSeconds <= end)) return false
    }

    if (endDateRange[0] && endDateRange[1]) {
      if (!order.completedTime?.seconds) return false
      const completedSeconds = order.completedTime.seconds
      const start = endDateRange[0].startOf("day").unix()
      const end = endDateRange[1].endOf("day").unix()
      if (!(completedSeconds >= start && completedSeconds <= end)) return false
    }

    return true
  })

  let jobFilters =
    activeOrders &&
    activeOrders.reduce((accumulator, item) => {
      const productName = item.selectedProduct.product.productName
      const existingItem = accumulator.find((filterItem) => filterItem.text === productName)
      if (existingItem) {
        existingItem.value = productName
      } else {
        accumulator.push({ text: productName, value: productName })
      }
      return accumulator
    }, [])

  let clientFIlters =
    activeOrders &&
    activeOrders.reduce((accumulator, item) => {
      const clientName = item.client.client
      const existingItem = accumulator.find((filterItem) => filterItem.text === clientName)
      if (existingItem) {
        existingItem.value = clientName
      } else {
        accumulator.push({ text: clientName, value: clientName })
      }
      return accumulator
    }, [])

  const sortedOrders =
    props.activeKey === 7
      ? [...filteredOrders].sort((a, b) => {
          if (a.completedTime && b.completedTime)
            return b.completedTime.seconds - a.completedTime.seconds
          else if (a.completedTime && !b.completedTime) return -1
          else if (!a.completedTime && b.completedTime) return 1
          else return 0
        })
      : filteredOrders

  const defaultColumns = [
    {
      title: "Nr.",
      dataIndex: "orderNumber",
      align: "left",
      width: 200,
      sorter: (a, b) => a.orderNumber - b.orderNumber,
      render: (value, record) => OrderAndPrefix(record),
    },
    {
      title: "Job Name",
      dataIndex: ["selectedProduct", "product", "productName"],
      align: "left",
      ellipsis: { showTitle: false },
      filters: jobFilters,
      width: 400,
      onFilter: (value, record) => record.selectedProduct.product.productName.startsWith(value),
      filterSearch: true,
      render: (value) => (
        <Tooltip placement="topLeft" title={value}>
          {value}
        </Tooltip>
      ),
    },
    {
      title: "Client",
      dataIndex: ["client", "client"],
      align: "left",
      ellipsis: { showTitle: false },
      filters: clientFIlters,
      width: 220,
      onFilter: (value, record) => record.client.client.startsWith(value),
      filterSearch: true,
      render: (value) => (
        <Tooltip placement="topLeft" title={value}>
          {value}
        </Tooltip>
      ),
    },
    {
      title: "Manager",
      dataIndex: "user",
      align: "left",
      width: 100,
      filters: designerUsersFilter,
      onFilter: (value, record) => record.user.startsWith(value),
      filterSearch: true,
      render: (value) => value?.split("@")[0] ?? "-",
    },
    {
      title: "Machine",
      dataIndex: ["selectedProduct", "machine"],
      align: "right",
      width: 100,
      filters: [
        { text: "Arsoma", value: "Arsoma" },
        { text: "Viva", value: "Viva" },
        { text: "Cutter", value: "Cutter" },
        { text: "Tape", value: "Tape" },
      ],
      onFilter: (value, record) => record.selectedProduct.machine.startsWith(value),
      filterSearch: true,
      render: (value) => returnProperMachineTag(value),
      key: "machine",
    },
    {
      title: "Plate",
      dataIndex: ["selectedPlate", "code"],
      align: "left",
      width: 100,
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input.Search
            placeholder="Search plate..."
            value={selectedKeys[0]}
            onChange={(e) => {
              setSelectedKeys(e.target.value ? [e.target.value] : [])
              if (!e.target.value) clearFilters()
            }}
            onSearch={() => confirm()}
            onPressEnter={() => confirm()}
            allowClear
            style={{ width: 180 }}
          />
        </div>
      ),
      filterIcon: (filtered) => (
        <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
      ),
      onFilter: (value, record) =>
        record.selectedPlate?.code?.toLowerCase().includes(value.toLowerCase()),
      render: (value, record) => {
        const handleOpenPdf = () => {
          const layoutDesign = record.selectedPlate?.layoutDesign
          const machine = record.selectedProduct?.machine?.toLowerCase()
          if (!layoutDesign || !machine) return

          const storage = getStorage()
          const fileRef = ref(storage, `${machine}Plates/${value}/${layoutDesign}`)

          getDownloadURL(fileRef)
            .then((url) => window.open(url, "_blank"))
            .catch(() => message.error("Could not load PDF"))
        }

        return value ? <Link onClick={handleOpenPdf}>{value}</Link> : "-"
      },
    },
    {
      title: "Technical",
      dataIndex: "technicalPage",
      align: "left",
      width: 100,
      render: (value) => <TechnicalPageModal data={value} />,
    },
    {
      title: "Details",
      align: "center",
      width: 100,
      render: (value, re) => (
        <Text>
          <Redirect state={value} to={value.id}>
            View
          </Redirect>
        </Text>
      ),
    },
    {
      title: "Started",
      align: "center",
      dataIndex: "created",
      key: "created",
      width: 100,
      filterDropdown: () => (
        <div style={{ padding: 8 }}>
          <RangePicker
            onChange={(dates) => setDateRange(dates ? dates : [null, null])}
            format="DD/MM/YYYY"
            allowClear
          />
        </div>
      ),
      filterIcon: () => <span style={{ color: dateRange[0] ? "#1890ff" : undefined }}>📅</span>,
      sorter: (a, b) => {
        if (a.created && b.created) return a.created.seconds - b.created.seconds
        else if (a.created && !b.created) return -1
        else if (!a.created && b.created) return 1
        else return 0
      },
      render: (value, record) => orderStartDate(value, record),
    },
    ...(props.activeKey === 7
      ? [
          {
            title: "Ended",
            align: "center",
            dataIndex: "completedTime",
            key: "completedTime",
            width: 100,
            filterDropdown: () => (
              <div style={{ padding: 8 }}>
                <RangePicker
                  onChange={(dates) => setEndDateRange(dates ? dates : [null, null])}
                  format="DD/MM/YYYY"
                  allowClear
                />
              </div>
            ),
            filterIcon: () => (
              <span style={{ color: endDateRange[0] ? "#1890ff" : undefined }}>📅</span>
            ),
            sorter: (a, b) => {
              if (a.completedTime && b.completedTime)
                return a.completedTime.seconds - b.completedTime.seconds
              else if (a.completedTime && !b.completedTime) return -1
              else if (!a.completedTime && b.completedTime) return 1
              else return 0
            },
            render: (value, record) => orderEndDate(value, record),
          },
        ]
      : []),
  ]

  return (
    <>
      <Table
        style={{ marginTop: "15px" }}
        size="small"
        loading={activeOrdersLoading}
        rowKey={(record) => record.id}
        columns={defaultColumns}
        dataSource={sortedOrders}
        pagination={{ defaultPageSize: "100", showSizeChanger: true }}
      />
    </>
  )
}
